import { Box, Stack, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import React, { useEffect } from "react";
import { updateAlertMetric } from "../../store/reducers/analytics/maintenance-schedule.metrics";

import { DamsColors } from "../styles/DamsColors";

import { cardStyles } from "./CountMetricsChart";
import { BadgeLabel } from "../Label";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";

type AlertType = "upcoming" | "overdue";

type AlertSeverity = "upcoming" | "due-soon" | "due-today" | "overdue";

const DUE_SOON_THRESHOLD_DAYS = 30;

interface Props {
  type: AlertType;
  title: string;
  serialNumber: string;
  description: string;
  days?: number;
}

const getAlertSeverity = (type: AlertType, days?: number): AlertSeverity => {
  if (type === "overdue") return "overdue";
  if (days === undefined || days === null) return "upcoming";
  if (days <= 0) return "due-today";
  if (days <= DUE_SOON_THRESHOLD_DAYS) return "due-soon";
  return "upcoming";
};

const alertSeverityConfig: Record<
  AlertSeverity,
  {
    label: string;
    badgeColor: "red" | "orange" | "yellow" | "blue";
    colors: { border: string; background: string; text: string };
    Icon: typeof EventOutlinedIcon;
  }
> = {
  overdue: {
    label: "Overdue",
    badgeColor: "red",
    colors: DamsColors.Badge.Red,
    Icon: ErrorOutlineRoundedIcon,
  },
  "due-today": {
    label: "Due today",
    badgeColor: "orange",
    colors: DamsColors.Badge.Orange,
    Icon: WarningAmberRoundedIcon,
  },
  "due-soon": {
    label: "Due soon",
    badgeColor: "yellow",
    colors: DamsColors.Badge.Yellow,
    Icon: ScheduleRoundedIcon,
  },
  upcoming: {
    label: "Upcoming",
    badgeColor: "blue",
    colors: DamsColors.Badge.Blue,
    Icon: EventOutlinedIcon,
  },
};

export const DashboardAlertCard = ({
  type,
  title,
  serialNumber,
  description,
  days,
}: Props) => {
  const { label, badgeColor, colors, Icon } =
    alertSeverityConfig[getAlertSeverity(type, days)];

  return (
    <Box
      sx={{
        width: "100%",
        p: 2,
        borderRadius: 3,
        backgroundColor: colors.background,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Stack direction={"row"} spacing={1.5} alignItems={"flex-start"}>
        <Box
          sx={{
            ...styles.iconTile,
            color: colors.text,
            border: `1px solid ${colors.border}`,
          }}
        >
          <Icon sx={{ fontSize: 20 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            width={"100%"}
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
            spacing={1}
          >
            <Typography
              sx={{ ...styles.title, color: DamsColors.Text.PrimaryText }}
            >
              {title}
            </Typography>

            <BadgeLabel title={label} colorType={badgeColor} />
          </Stack>

          <Typography sx={styles.subtitle}>
            Serial Number : {serialNumber}
          </Typography>
          <Typography sx={{ ...styles.subtitle, fontSize: 14 }}>
            {description}
          </Typography>
          <Typography sx={{ ...styles.subtitle }}>
            {" "}
            {type === "overdue" ? "Days overdue" : "Days remaining"} :{" "}
            {days ?? "-"}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export const DashboardAlertLayout = ({
  children,
  type,
}: {
  children: React.ReactNode;
  type: AlertType;
}) => {
  return (
    <Box
      sx={{
        flex: 1,
        maxHeight: 400,
        overflowY: "auto",
        pr: 1,
      }}
    >
      {children}
    </Box>
  );
};

export const DashboardAlertController = () => {
  const { alerts } = useAppSelector((d) => d.maintenanceSchedulesMetrics);

  const dispatch = useAppDispatch();
  const fetch = async () => {
    dispatch(updateAlertMetric());
  };

  useEffect(() => {
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Box py={5} sx={{ ...cardStyles.layout, width: "100%" }}>
      <Typography sx={cardStyles.title}>Recent maintenance alerts</Typography>

      {!alerts.loading &&
        alerts.overdue.length + alerts.upcoming.length === 0 && (
          <Stack
            width={"100%"}
            alignItems={"center"}
            justifyContent={"center"}
            height={400}
          >
            <Typography sx={{ ...styles.subtitle, fontSize: 15 }}>
              No recent maintenance schedule alerts yet
            </Typography>
          </Stack>
        )}
      <Stack direction={"row"} spacing={2}>
        <DashboardAlertLayout type="upcoming">
          <Stack direction={"column"} spacing={2}>
            {[...alerts.upcoming]
              .sort(
                (a, b) =>
                  (a.days_remaining ?? Infinity) -
                  (b.days_remaining ?? Infinity),
              )
              .map((data, index) => {
                return (
                  <DashboardAlertCard
                    key={`${data.asset_serial_number}-${index}`}
                    type="upcoming"
                    title={data.asset_category}
                    description={data.service_remarks}
                    days={data.days_remaining}
                    serialNumber={data.asset_serial_number}
                  />
                );
              })}
          </Stack>
        </DashboardAlertLayout>

        <DashboardAlertLayout type="overdue">
          <Stack direction={"column"} spacing={2}>
            {[...alerts.overdue]
              .sort((a, b) => (b.days_overdue ?? 0) - (a.days_overdue ?? 0))
              .map((data, index) => {
                return (
                  <DashboardAlertCard
                    key={`${data.asset_serial_number}-${index}`}
                    type="overdue"
                    title={data.asset_category}
                    description={data.service_remarks}
                    days={data.days_overdue}
                    serialNumber={data.asset_serial_number}
                  />
                );
              })}
          </Stack>
        </DashboardAlertLayout>
      </Stack>
    </Box>
  );
};

const styles = {
  iconTile: {
    width: 36,
    height: 36,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 2,
    backgroundColor: "white",
  },
  title: {
    fontFamily: "Inter",
    fontSize: 15,
    fontWeight: 500,
    noWrap: true,
    textAlign: "left" as const,
  },
  subtitle: {
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 400,
    noWrap: true,
    textAlign: "left" as const,
  },
};
